/**
 * Sales coaching, as the backend serves it. Mirrors of
 * `packages/models/src/coaching/{library-api,setup-cards,start}.ts` and the
 * generated `Coaching*` enums; keep them in step by hand.
 */

import { OrgUserRoleType } from '@euler/model/enum';

export enum CoachingCaseFormat {
  Full = 'full',
  Drill = 'drill',
}

export enum CoachingDifficulty {
  Foundation = 1,
  Intermediate = 2,
  Advanced = 3,
  Expert = 4,
}

export const kCoachingDifficulties = [
  CoachingDifficulty.Foundation,
  CoachingDifficulty.Intermediate,
  CoachingDifficulty.Advanced,
  CoachingDifficulty.Expert,
] as const;

export enum CoachingChannel {
  Counter = 'counter',
  WorkshopWalkthrough = 'workshop_walkthrough',
  Phone = 'phone',
  Wechat = 'wechat',
}

export enum CoachingRelationshipStage {
  Prospective = 'prospective',
  FirstTime = 'first_time',
  Active = 'active',
  AtRisk = 'at_risk',
  Inactive = 'inactive',
  Returning = 'returning',
}

export enum CoachingOpeningSide {
  Customer = 'customer',
  Trainee = 'trainee',
}

export enum CoachingCaseKind {
  System = 'system',
  Organization = 'organization',
  Story = 'story',
}

export enum CoachingCaseLength {
  Short = 'short',
  Medium = 'medium',
  Long = 'long',
}

export interface CoachingNamedItem {
  slug: string;
  name: string;
}

export const kCoachingKeywordMaxLength = 50;

export interface CoachingCaseBrowseRequest {
  keyword?: string;
  category?: string;
  collection?: string;
  tags?: string[];
  formats?: CoachingCaseFormat[];
  roles?: OrgUserRoleType[];
  criteria?: string[];
  difficulties?: CoachingDifficulty[];
  channels?: CoachingChannel[];
  relationshipStages?: CoachingRelationshipStage[];
  openingSides?: CoachingOpeningSide[];
  lengths?: CoachingCaseLength[];
  offset?: number;
  limit?: number;
}

export interface CoachingCaseCard {
  id: string;
  ref: string;
  title: string;
  caption: string;
  format: CoachingCaseFormat;
  category: CoachingNamedItem;
  parentCategory: CoachingNamedItem;
  tags: CoachingNamedItem[];
  levels: CoachingDifficulty[];
  expectedTurns: { min: number; max: number };
}

export interface CoachingCaseBrowseResult {
  cases: CoachingCaseCard[];
  total: number;
}

export interface CoachingCaseDetail extends CoachingCaseCard {
  additionalCategories: CoachingNamedItem[];
  traineeRoles: OrgUserRoleType[];
  channels: CoachingChannel[];
  objective: string;
  initiallyKnown: string[];
  /** `slug` is the criterion ref. */
  criteria: CoachingNamedItem[];
  /** The first related cases; `relatedTotal` counts them all. */
  related: CoachingCaseCard[];
  relatedTotal: number;
}

/** Every case related to one, for its 相关案例 page. */
export interface CoachingRelatedCasesResult {
  cases: CoachingCaseCard[];
}

export interface CoachingLibraryCatalogue {
  categories: (CoachingNamedItem & { children: CoachingNamedItem[] })[];
  collections: (CoachingNamedItem & {
    description: string | null;
    caseCount: number;
  })[];
  criterionAreas: (CoachingNamedItem & { criteria: CoachingNamedItem[] })[];
  /** The tags the most published cases carry, at most 25, most first. */
  tags: (CoachingNamedItem & { caseCount: number })[];
}

export enum CoachingSuggestionReasonKind {
  CollectionNext = 'collection_next',
  Role = 'role',
  /** Practice for the area where the trainee's practices score lowest. */
  WeakestArea = 'weakest_area',
}

export type CoachingSuggestionReason =
  | {
      kind: CoachingSuggestionReasonKind.CollectionNext;
      collection: CoachingNamedItem;
    }
  | { kind: CoachingSuggestionReasonKind.Role; role: OrgUserRoleType }
  | {
      kind: CoachingSuggestionReasonKind.WeakestArea;
      area: CoachingNamedItem;
      criterion: CoachingNamedItem;
    };

export interface CoachingSuggestion {
  case: CoachingCaseCard;
  difficulty: CoachingDifficulty;
  reason: CoachingSuggestionReason;
}

export interface CoachingSuggestionsResult {
  suggestions: CoachingSuggestion[];
}

export interface CoachingStoryDraft {
  title: string;
  summary: string;
  situation: string;
  customerDecision: string;
  traineeKnows: string[];
  channel: CoachingChannel;
  openingSide: CoachingOpeningSide;
  opening: string;
  goodEnding: string;
  assumed: string[];
  singleMoment: boolean;
}

export const COACHING_SETUP_CARD_FENCE = 'coaching_setup_card';

export enum CoachingSetupCardKind {
  Story = 'story',
  DrillOffer = 'drill_offer',
  Difficulty = 'difficulty',
  Summary = 'summary',
}

export type CoachingSetupCard =
  | {
      kind: CoachingSetupCardKind.Story;
      setupId: string;
      story: CoachingStoryDraft;
    }
  | { kind: CoachingSetupCardKind.DrillOffer; setupId: string; title: string }
  | {
      kind: CoachingSetupCardKind.Difficulty;
      setupId: string;
      title: string;
      subject: 'case' | 'story';
      levels: CoachingDifficulty[];
    }
  | {
      kind: CoachingSetupCardKind.Summary;
      setupId: string;
      /** Sent back by Start: the server starts only an unchanged setup. */
      settledKey: string;
      title: string;
      summary: string;
      format: CoachingCaseFormat;
      difficulty: CoachingDifficulty;
      channel: CoachingChannel | null;
      openingSide: CoachingOpeningSide | null;
      expectedTurns: { min: number; max: number } | null;
      traineeKnows: string[];
      assumed: string[];
    };

export enum CoachingCustomerGender {
  Male = 'male',
  Female = 'female',
}

export enum CoachingAttemptOrigin {
  Free = 'free',
  Task = 'task',
  Exam = 'exam',
}

export enum CoachingAttemptStatus {
  Ready = 'ready',
  Active = 'active',
  Ended = 'ended',
  Assessed = 'assessed',
  AssessmentFailed = 'assessment_failed',
  Abandoned = 'abandoned',
}

/** How a full case ended. */
export enum CoachingEnding {
  Purchase = 'purchase',
  WorkAuthorized = 'work_authorized',
  Booking = 'booking',
  FurtherAssessment = 'further_assessment',
  AgreedFollowUp = 'agreed_follow_up',
  InformedRefusal = 'informed_refusal',
  Resolution = 'resolution',
  Escalation = 'escalation',
  NoFurtherContact = 'no_further_contact',
  CustomerLeft = 'customer_left',
  Undecided = 'undecided',
}

/** How a drill ended. */
export enum CoachingDrillResult {
  Demonstrated = 'demonstrated',
  PartiallyDemonstrated = 'partially_demonstrated',
  Missed = 'missed',
  CriticalError = 'critical_error',
  TurnLimitReached = 'turn_limit_reached',
}

export interface CoachingAttemptView {
  id: string;
  caseId: string;
  caseKind: CoachingCaseKind;
  title: string;
  format: CoachingCaseFormat;
  difficulty: CoachingDifficulty;
  origin: CoachingAttemptOrigin;
  status: CoachingAttemptStatus;
  variationLabel: string;
  /** Once assessed. */
  stars: number | null;
  ending: CoachingEnding | null;
  drillResult: CoachingDrillResult | null;
  createdAt: string;
}

export interface CoachingAttemptListRequest {
  /** Attempts older than this one: the last id of the previous page. */
  beforeId?: string;
  limit?: number;
}

export interface CoachingAttemptListResult {
  attempts: CoachingAttemptView[];
}

/** What Start and replay answer. */
export interface CoachingSetupStartResult {
  attempt: CoachingAttemptView;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isLevel(value: unknown): value is CoachingDifficulty {
  return kCoachingDifficulties.includes(value as CoachingDifficulty);
}

function texts(value: unknown): string[] | null {
  return Array.isArray(value) && value.every(x => typeof x === 'string')
    ? (value as string[])
    : null;
}

function enumValue<T extends string>(
  value: unknown,
  values: Record<string, T>,
): T | null {
  return Object.values(values).includes(value as T) ? (value as T) : null;
}

function parseStory(value: unknown): CoachingStoryDraft | null {
  if (!isRecord(value)) return null;
  const traineeKnows = texts(value.traineeKnows);
  const assumed = texts(value.assumed);
  // Display-only: a value a newer server added still shows the story, with
  // a fallback label, rather than failing the whole card.
  const channel =
    typeof value.channel === 'string' && value.channel
      ? (value.channel as CoachingChannel)
      : null;
  const openingSide =
    typeof value.openingSide === 'string' && value.openingSide
      ? (value.openingSide as CoachingOpeningSide)
      : null;
  const fields = [
    'title',
    'summary',
    'situation',
    'customerDecision',
    'opening',
    'goodEnding',
  ] as const;
  if (
    fields.some(field => typeof value[field] !== 'string') ||
    !traineeKnows ||
    !assumed ||
    !channel ||
    !openingSide ||
    typeof value.singleMoment !== 'boolean'
  )
    return null;
  return {
    title: value.title as string,
    summary: value.summary as string,
    situation: value.situation as string,
    customerDecision: value.customerDecision as string,
    opening: value.opening as string,
    goodEnding: value.goodEnding as string,
    traineeKnows,
    assumed,
    channel,
    openingSide,
    singleMoment: value.singleMoment,
  };
}

/** A `coaching_setup_card` fence's payload; null when it is not one. */
export function parseCoachingSetupCard(
  value: unknown,
): CoachingSetupCard | null {
  if (!isRecord(value) || typeof value.setupId !== 'string') return null;
  const setupId = value.setupId;
  const title = typeof value.title === 'string' ? value.title : null;
  switch (value.kind) {
    case CoachingSetupCardKind.Story: {
      const story = parseStory(value.story);
      return story ? { kind: value.kind, setupId, story } : null;
    }
    case CoachingSetupCardKind.DrillOffer:
      return title ? { kind: value.kind, setupId, title } : null;
    case CoachingSetupCardKind.Difficulty:
      return title &&
        (value.subject === 'case' || value.subject === 'story') &&
        Array.isArray(value.levels) &&
        value.levels.every(isLevel)
        ? {
            kind: value.kind,
            setupId,
            title,
            subject: value.subject,
            levels: value.levels as CoachingDifficulty[],
          }
        : null;
    case CoachingSetupCardKind.Summary: {
      const format = enumValue(value.format, CoachingCaseFormat);
      const settledKey =
        typeof value.settledKey === 'string' && value.settledKey
          ? value.settledKey
          : null;
      const traineeKnows = texts(value.traineeKnows);
      const assumed = texts(value.assumed);
      const turns = isRecord(value.expectedTurns) ? value.expectedTurns : null;
      if (
        !title ||
        !settledKey ||
        typeof value.summary !== 'string' ||
        !format ||
        !isLevel(value.difficulty) ||
        !traineeKnows ||
        !assumed
      )
        return null;
      return {
        kind: value.kind,
        setupId,
        settledKey,
        title,
        summary: value.summary,
        format,
        difficulty: value.difficulty,
        channel: enumValue(value.channel, CoachingChannel),
        openingSide: enumValue(value.openingSide, CoachingOpeningSide),
        expectedTurns:
          turns &&
          typeof turns.min === 'number' &&
          typeof turns.max === 'number'
            ? { min: turns.min, max: turns.max }
            : null,
        traineeKnows,
        assumed,
      };
    }
    default:
      return null;
  }
}
